import isEmpty from 'lodash/isEmpty';
import { NextPage, NextPageContext } from 'next';
import React from 'react';

import { setLocation } from '@/actions/rentals';
import HeadMeta from '@/components/static-pages/HeadMeta';
import ParkTemplate, { IParkAlert, ParkGuidePageData } from '@/components/templates/ParkTemplate';
import apiRequest from '@/services/apiRequest';
import getPageData from '@/services/getPageData.js';
import rentalsRequest from '@/services/rentals-request';
import { IStore } from '@/utility/redux/store';

const Park: NextPage<ParkGuidePageData> = props => {
  const { title, seo_description, canonical_url } = props.metadata;
  return (
    <React.Fragment>
      <HeadMeta
        title={title}
        description={seo_description}
        canonicalUrl={canonical_url}
        noindex={!!props.fallback}
      />
      <ParkTemplate {...props} />
    </React.Fragment>
  );
};

Park.getInitialProps = async ({
  req,
  res,
  query,
  reduxStore,
}: NextPageContext & { reduxStore: IStore }) => {
  const pageData = await getPageData({ req, res, query });

  const { lat, lon } = pageData?.metadata?.location ?? {};
  const { nps_park_code } = pageData?.metadata ?? {};

  const [rentals, alerts] = await Promise.all([
    rentalsRequest({ lat, lon }),
    alertRequest(nps_park_code),
  ]);

  await reduxStore.dispatch(setLocation(lat, lon));

  return { ...pageData, rentals, alerts };
};

export default Park;

// https://www.nps.gov/subjects/developer/api-documentation.htm#/alerts/getAlerts
interface AlertsResponse {
  total: number;
  data: IParkAlert[];
  limit: number;
  start: number;
}

const alertRequest = async (nps_park_code: string): Promise<IParkAlert[]> => {
  if (isEmpty(nps_park_code)) {
    return [];
  }

  try {
    const alertsData = await apiRequest<AlertsResponse>({
      url: `https://developer.nps.gov/api/v1/alerts?parkCode=${nps_park_code}&api_key=${process.env.npsApiKey}`,
    });
    return alertsData?.data || [];
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error fetching alerts', err);
    return [];
  }
};
